import CoinIcon from '../components/CoinIcon';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { FiCheck, FiX, FiShield, FiStar, FiTrendingUp, FiUsers, FiImage, FiSearch, FiMessageCircle, FiShoppingBag, FiLayout, FiZap, FiClock } from 'react-icons/fi';

const PLAN_FEATURE_ICONS = {
  commission: CoinIcon,
  portfolio: FiImage,
  search_ranking: FiSearch,
  request_board: FiShoppingBag,
  community: FiUsers,
  event_search: FiSearch,
  analytics: FiTrendingUp,
  verified_badge: FiShield,
  top_vendor_badge: FiStar,
  direct_outreach: FiMessageCircle,
  rsvp: FiUsers,
  invitations: FiLayout,
  tickets: CoinIcon,
  whatsapp: FiMessageCircle,
  seating: FiLayout,
  budget: CoinIcon,
  vendor_matching: FiZap,
};

function formatZAR(amount) {
  if (amount == null || isNaN(amount)) return null;
  return 'R' + Number(amount).toFixed(0);
}

function PlanCard({ plan, isCurrent, isPopular, onUpgrade, upgrading, canUpgrade }) {
  const price = plan.price_monthly ? formatZAR(plan.price_monthly) : null;
  const tierLabel = plan.tier.charAt(0).toUpperCase() + plan.tier.slice(1);

  const features = [];

  if (plan.plan_type === 'vendor') {
    features.push({
      key: 'commission',
      label: `${plan.commission_rate}% commission`,
      icon: 'commission',
      positive: plan.commission_rate <= 3,
      neutral: plan.commission_rate === 8,
    });
    features.push({
      key: 'portfolio',
      label: plan.max_portfolio_images ? `${plan.max_portfolio_images} portfolio images` : 'Unlimited portfolio images',
      icon: 'portfolio',
      positive: true,
    });
    features.push({
      key: 'search',
      label: plan.search_ranking === 2 ? 'Top search ranking' : plan.search_ranking === 1 ? 'Priority search ranking' : 'Standard search ranking',
      icon: 'search_ranking',
      positive: plan.search_ranking >= 1,
      neutral: plan.search_ranking === 0,
    });
    if (plan.verified_badge !== null) {
      features.push({
        key: 'verified',
        label: 'Verified badge',
        icon: 'verified_badge',
        positive: plan.verified_badge,
        negative: !plan.verified_badge,
      });
    }
    if (plan.top_vendor_badge !== null) {
      features.push({
        key: 'top_vendor',
        label: 'Top Vendor badge',
        icon: 'top_vendor_badge',
        positive: plan.top_vendor_badge,
        negative: !plan.top_vendor_badge,
      });
    }
    features.push({
      key: 'community',
      label: plan.community_access ? 'Full community access' : 'Community locked',
      icon: 'community',
      positive: plan.community_access,
      negative: !plan.community_access,
    });
    features.push({
      key: 'event_search',
      label: plan.event_search ? 'Browse & search events' : 'Event search locked',
      icon: 'event_search',
      positive: plan.event_search,
      negative: !plan.event_search,
    });
    features.push({
      key: 'request_board',
      label: plan.claim_lead_price
        ? `Claim leads at ${formatZAR(plan.claim_lead_price)}/lead`
        : 'Browse request board only',
      icon: 'request_board',
      positive: !!plan.claim_lead_price,
      neutral: !plan.claim_lead_price,
    });
    if (plan.direct_outreach !== null) {
      features.push({
        key: 'outreach',
        label: 'Direct host outreach',
        icon: 'direct_outreach',
        positive: plan.direct_outreach,
        negative: !plan.direct_outreach,
      });
    }
    features.push({
      key: 'loyalty',
      label: `${plan.loyalty_multiplier}x loyalty points`,
      icon: 'analytics',
      positive: plan.loyalty_multiplier >= 2,
      neutral: plan.loyalty_multiplier === 1,
    });
    features.push({
      key: 'analytics',
      label: plan.analytics_access ? 'Advanced analytics' : 'Basic analytics',
      icon: 'analytics',
      positive: plan.analytics_access,
      neutral: !plan.analytics_access,
    });
  } else {
    // Host plan features
    features.push({
      key: 'rsvp',
      label: plan.rsvp_limit ? `${plan.rsvp_limit} RSVPs` : 'Unlimited RSVPs',
      icon: 'rsvp',
      positive: !plan.rsvp_limit || plan.rsvp_limit >= 200,
      neutral: plan.rsvp_limit === 50,
    });
    features.push({
      key: 'invitations',
      label: plan.invitation_designs ? `${plan.invitation_designs} invitation designs` : 'Unlimited + AI custom designs',
      icon: 'invitations',
      positive: !plan.invitation_designs || plan.invitation_designs >= 20,
      neutral: plan.invitation_designs === 10,
    });
    features.push({
      key: 'registry',
      label: 'Gift registry',
      icon: 'budget',
      positive: true,
    });
    features.push({
      key: 'marketplace',
      label: plan.vendor_matching ? 'Full vendor marketplace' + (plan.tier === 'premium' ? ' + priority matching' : '') : 'Vendor marketplace',
      icon: 'vendor_matching',
      positive: true,
    });
    features.push({
      key: 'tickets',
      label: plan.ticket_sales_limit ? `Ticket sales up to ${formatZAR(plan.ticket_sales_limit)}` : 'Unlimited ticket sales',
      icon: 'tickets',
      positive: !plan.ticket_sales_limit || plan.ticket_sales_limit >= 50000,
      neutral: plan.ticket_sales_limit === 5000,
    });
    features.push({
      key: 'whatsapp',
      label: plan.whatsapp_messages ? `${plan.whatsapp_messages} WhatsApp messages` : `${plan.whatsapp_messages || 500} WhatsApp messages`,
      icon: 'whatsapp',
      positive: plan.whatsapp_messages >= 200,
      neutral: plan.whatsapp_messages === 50,
    });
    features.push({
      key: 'seating',
      label: plan.seating_planner ? 'Seating planner' : 'Seating planner locked',
      icon: 'seating',
      positive: plan.seating_planner,
      negative: !plan.seating_planner,
    });
    features.push({
      key: 'budget',
      label: plan.budget_tracker ? 'Budget tracker' : 'Budget tracker locked',
      icon: 'budget',
      positive: plan.budget_tracker,
      negative: !plan.budget_tracker,
    });
  }

  return (
    <div style={{
      flex: '1 1 280px',
      maxWidth: '340px',
      minWidth: '260px',
      borderRadius: '20px',
      background: isCurrent ? 'rgba(227,66,92,0.08)' : 'rgba(255,255,255,0.03)',
      border: isCurrent ? '2px solid #E3425C' : isPopular ? '2px solid rgba(212,168,83,0.25)' : '1px solid rgba(255,255,255,0.06)',
      boxShadow: isPopular && !isCurrent ? '0 0 40px rgba(212,168,83,0.08)' : 'none',
      padding: '32px 24px 24px',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      transition: 'transform 0.2s, border-color 0.2s',
    }}>
      {isPopular && !isCurrent && (
        <div style={{
          position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
          background: '#E3425C', color: '#2D1F2B', fontSize: '11px', fontWeight: 700,
          letterSpacing: '1px', textTransform: 'uppercase',
          padding: '4px 16px', borderRadius: '20px'
        }}>Most Popular</div>
      )}
      {isCurrent && (
        <div style={{
          position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
          background: '#10B981', color: '#fff', fontSize: '11px', fontWeight: 700,
          letterSpacing: '1px', textTransform: 'uppercase',
          padding: '4px 16px', borderRadius: '20px'
        }}>Current Plan</div>
      )}

      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <h3 style={{
          fontSize: '20px', fontWeight: 700, color: '#FFFFFF',
          fontFamily: 'Inter, sans-serif', margin: '0 0 8px', textTransform: 'capitalize'
        }}>{tierLabel}</h3>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '2px' }}>
          {price ? (
            <>
              <span style={{ fontSize: '36px', fontWeight: 800, color: '#FFFFFF', fontFamily: 'Inter, sans-serif' }}>{price}</span>
              <span style={{ fontSize: '14px', color: '#FFFFFF', opacity: 0.5, fontFamily: 'Inter, sans-serif' }}>
                {plan.plan_type === 'vendor' ? '/mo' : '/event'}
              </span>
            </>
          ) : (
            <span style={{ fontSize: '36px', fontWeight: 800, color: '#FFFFFF', fontFamily: 'Inter, sans-serif' }}>FREE</span>
          )}
        </div>
        {plan.plan_type === 'vendor' && price && (
          <div style={{ fontSize: '11px', color: '#FFFFFF', opacity: 0.4, fontFamily: 'Inter, sans-serif', marginTop: '-12px', marginBottom: '12px' }}>
            Cancel anytime
          </div>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
        {features.map(f => {
          const Icon = PLAN_FEATURE_ICONS[f.icon] || FiCheck;
          return (
            <div key={f.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px' }}>
              {f.negative ? (
                <FiX size={16} style={{ color: '#FF6B6B', marginTop: '1px', flexShrink: 0 }} />
              ) : (
                <FiCheck size={16} style={{ color: f.neutral ? '#888' : '#10B981', marginTop: '1px', flexShrink: 0 }} />
              )}
              <span style={{
                color: f.negative ? '#FF6B6B' : f.neutral ? '#888' : '#D4D4D8',
                fontFamily: 'Inter, sans-serif', lineHeight: 1.4, opacity: f.negative ? 0.8 : f.neutral ? 0.7 : 1
              }}>{f.label}</span>
            </div>
          );
        })}
      </div>

      <button
        disabled={isCurrent || !canUpgrade || upgrading}
        onClick={() => onUpgrade(plan.tier)}
        style={{
          width: '100%', padding: '14px', borderRadius: '14px', border: 'none',
          background: isCurrent ? 'rgba(255,255,255,0.06)' : '#E3425C',
          color: isCurrent ? '#888' : '#2D1F2B',
          fontSize: '15px', fontWeight: 700, fontFamily: 'Inter, sans-serif',
          cursor: isCurrent || !canUpgrade || upgrading ? 'not-allowed' : 'pointer',
          opacity: isCurrent ? 0.5 : upgrading ? 0.7 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          transition: 'background 0.2s',
        }}>
        {upgrading ? (
          <>
            <div style={{ width: '16px', height: '16px', border: '2px solid #2D1F2B33', borderTop: '2px solid #2D1F2B', borderRadius: '50%', animation: 'ggrSpin 0.8s linear infinite' }} />
            Processing...
          </>
        ) : isCurrent ? 'Current Plan' : price ? `Upgrade to ${tierLabel}` : 'Downgrade to Free'}
      </button>
    </div>
  );
}

export default function UpgradePlans() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planType = searchParams.get('type') || 'host';

  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upgrading, setUpgrading] = useState(null); // tier being upgraded
  const [successMsg, setSuccessMsg] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoSubmitting, setPromoSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [verifying, setVerifying] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('reference') || params.get('trxref') || params.get('ref');
    const ozow = params.get('ozow');
    if (ref && ozow === 'success') return !!sessionStorage.getItem('ggr_sub_pending');
    return !!(ref && sessionStorage.getItem('ggr_sub_pending'));
  });

  useEffect(() => {
    Promise.all([
      api.get(`/subscription/plans?type=${planType}`),
      api.get('/subscription/status'),
    ])
      .then(([plansRes, statusRes]) => {
        setPlans(plansRes.data);
        setStatus(statusRes.data);
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load plans'))
      .finally(() => setLoading(false));
  }, [planType]);

  // Handle Ozow return (?ozow=success&ref=...) — webhook activates plan server-side
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    const ozow = params.get('ozow');
    if (ref && ozow === 'success') {
      const pendingRaw = sessionStorage.getItem('ggr_sub_pending');
      const pending = pendingRaw ? JSON.parse(pendingRaw) : null;
      sessionStorage.removeItem('ggr_sub_pending');
      const type = pending?.type || 'host';
      setVerifying(true);

      const refresh = () => Promise.all([
        api.get('/subscription/status'),
        api.get('/auth/me'),
      ]).then(([sRes, meRes]) => {
        setStatus(sRes.data);
        updateUser(meRes.data);
        const tierNow = type === 'vendor'
          ? (sRes.data?.vendor?.tier || 'free')
          : (sRes.data?.host?.tier || 'free');
        if (tierNow !== 'free') {
          setVerifying(false);
          setSuccessMsg(`Successfully upgraded to ${tierNow.charAt(0).toUpperCase() + tierNow.slice(1)}!`);
          return true;
        }
        return false;
      }).catch(() => false);

      refresh();
      const interval = setInterval(async () => {
        const done = await refresh();
        if (done) clearInterval(interval);
      }, 3000);
      const timeout = setTimeout(() => {
        clearInterval(interval);
        setVerifying(false);
      }, 30000);
      window.history.replaceState({}, '', window.location.pathname + '?type=' + planType);
      return () => { clearInterval(interval); clearTimeout(timeout); };
    }
  }, []);

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await api.post('/redeem-promo', { code: promoCode.trim() });
      setSuccessMsg(res.data.message || 'Promo code redeemed! Refreshing...');
      setPromoCode('');
      // Refresh user context and status
      const [meRes, statusRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/subscription/status'),
      ]);
      updateUser(meRes.data);
      setStatus(statusRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to redeem promo code');
    } finally {
      setPromoSubmitting(false);
    }
  };

  const handleUpgrade = async (tier) => {
    const currentTier = planType === 'vendor'
      ? (status?.vendor?.tier || 'free')
      : (status?.host?.tier || 'free');
    if (tier === currentTier) return;

    setUpgrading(tier);
    setError(null);
    try {
      const initRes = await api.post('/ozow/subscription/initialize', {
        plan_type: planType,
        tier,
      });
      if (initRes.data?.success && initRes.data?.url && initRes.data?.params) {
        // Save pending info for verification on return
        sessionStorage.setItem('ggr_sub_pending', JSON.stringify({
          type: planType,
          tier,
          reference: initRes.data.reference,
        }));
        // Build and submit hidden form to pay.ozow.com
        const f = document.createElement('form');
        f.method = 'POST';
        f.action = initRes.data.url;
        f.style.display = 'none';
        Object.entries(initRes.data.params).forEach(([k, v]) => {
          const inp = document.createElement('input');
          inp.type = 'hidden'; inp.name = k; inp.value = v;
          f.appendChild(inp);
        });
        document.body.appendChild(f);
        f.submit();
        return; // Browser redirects to Ozow
      }
      setError(initRes.data?.error || 'Could not initialize payment. Please try again.');
      setUpgrading(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initialize payment');
      setUpgrading(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription? You will be moved to the free plan immediately.')) return;
    setCancelling(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await api.post('/subscription/cancel', { plan_type: planType });
      setSuccessMsg(res.data?.message || 'Subscription cancelled.');
      // Refresh status and user context
      const [meRes, statusRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/subscription/status'),
      ]);
      updateUser(meRes.data);
      setStatus(statusRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  if (loading || verifying) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0A0A0A' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', border: '3px solid rgba(227,66,92,0.15)', borderTop: '3px solid #E3425C', borderRadius: '50%', animation: 'ggrSpin 0.8s linear infinite', margin: '0 auto 20px' }} />
          <p style={{ color: '#FFFFFF', fontFamily: 'Inter, sans-serif', opacity: 0.6 }}>
            {verifying ? 'Verifying your payment...' : 'Loading plans...'}
          </p>
        </div>
        <style>{`@keyframes ggrSpin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  const currentTier = planType === 'vendor'
    ? (status?.vendor?.tier || 'free')
    : (status?.host?.tier || 'free');

  // Identify the "most popular" plan
  const popularTier = planType === 'vendor' ? 'pro' : 'standard';

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div style={{
        textAlign: 'center', padding: '48px 24px 24px',
        background: 'linear-gradient(180deg, rgba(227,66,92,0.08) 0%, transparent 100%)'
      }}>
        <button
          onClick={() => navigate(planType === 'vendor' ? '/vendor-dashboard' : '/dashboard')}
          style={{
            background: 'none', border: 'none', color: '#E3425C', cursor: 'pointer',
            fontSize: '14px', fontFamily: 'Inter, sans-serif', marginBottom: '20px',
            display: 'inline-flex', alignItems: 'center', gap: '6px'
          }}>
          &larr; Back to Dashboard
        </button>
        <h1 style={{
          fontSize: '36px', fontWeight: 700, color: '#FFFFFF',
          fontFamily: "'Playfair Display', serif", margin: '0 0 8px'
        }}>
          {planType === 'vendor' ? 'Vendor Plans' : 'Host Plans'}
        </h1>
        <p style={{
          fontSize: '16px', color: '#FFFFFF', opacity: 0.6, margin: 0,
          maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto'
        }}>
          {planType === 'vendor'
            ? 'Grow your business with lower commissions, more visibility, and premium features.'
            : 'Host bigger events with more RSVPs, designs, and advanced planning tools.'}
        </p>
      </div>

      {/* Success banner */}
      {successMsg && (
        <div style={{
          maxWidth: '900px', margin: '0 auto 0', padding: '14px 20px',
          background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)',
          borderRadius: '12px', color: '#10B981', fontSize: '14px', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Inter, sans-serif'
        }}>
          <FiCheck size={18} /> {successMsg}
          <button onClick={() => setSuccessMsg(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#10B981', cursor: 'pointer', fontSize: '16px' }}>&times;</button>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div style={{
          maxWidth: '900px', margin: '16px auto 0', padding: '14px 20px',
          background: 'rgba(255,68,68,0.12)', border: '1px solid rgba(255,68,68,0.25)',
          borderRadius: '12px', color: '#FF6B6B', fontSize: '14px',
          display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Inter, sans-serif'
        }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#FF6B6B', cursor: 'pointer', fontSize: '16px' }}>&times;</button>
        </div>
      )}

      {/* Active subscription / cancel anytime */}
      {currentTier !== 'free' && (
        <div style={{ maxWidth: '900px', margin: '16px auto 0', padding: '0 24px' }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between',
            background: 'rgba(227,66,92,0.08)', border: '1px solid rgba(227,66,92,0.25)',
            borderRadius: '12px', padding: '14px 20px',
          }}>
            <div>
              <div style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 600, fontFamily: 'Inter, sans-serif', textTransform: 'capitalize' }}>
                You're on the {currentTier} plan
              </div>
              <div style={{ color: '#FFFFFF', opacity: 0.55, fontSize: '12px', fontFamily: 'Inter, sans-serif', marginTop: '2px' }}>
                Billed monthly via Ozow. Cancel anytime — no lock-in, no penalties.
              </div>
            </div>
            <button
              onClick={handleCancel}
              disabled={cancelling}
              style={{
                background: 'transparent', border: '1px solid rgba(255,107,107,0.5)', borderRadius: '10px',
                padding: '9px 18px', color: '#FF6B6B', fontSize: '13px', fontWeight: 600,
                fontFamily: 'Inter, sans-serif', cursor: cancelling ? 'wait' : 'pointer',
                opacity: cancelling ? 0.6 : 1, whiteSpace: 'nowrap',
              }}
            >
              {cancelling ? 'Cancelling...' : 'Cancel subscription'}
            </button>
          </div>
        </div>
      )}

      {/* Promo code redemption — host tester package */}
      {planType === 'host' && (
        <div style={{
          maxWidth: '900px', margin: '0 auto', padding: '0 24px',
        }}>
          <div style={{
            display: 'flex', gap: '10px', alignItems: 'center',
            background: 'rgba(255,255,255,0.03)', borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.08)', padding: '12px 16px',
          }}>
            <span style={{ color: '#FFFFFF', opacity: 0.6, fontSize: '13px', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap' }}>
              Have a promo code?
            </span>
            <input
              type="text"
              placeholder="Enter code"
              value={promoCode}
              onChange={e => setPromoCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleRedeemPromo()}
              style={{
                flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px', padding: '10px 14px', color: '#FFFFFF', fontSize: '14px',
                fontFamily: 'Inter, sans-serif', outline: 'none',
              }}
            />
            <button
              onClick={handleRedeemPromo}
              disabled={promoSubmitting || !promoCode.trim()}
              style={{
                background: promoCode.trim() ? '#E3425C' : 'rgba(227,66,92,0.3)',
                border: 'none', borderRadius: '8px', padding: '10px 20px',
                color: '#2D1F2B', fontSize: '13px', fontWeight: 600,
                fontFamily: 'Inter, sans-serif', cursor: promoCode.trim() ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap', transition: 'background 0.2s',
              }}
            >
              {promoSubmitting ? 'Redeeming...' : 'Redeem'}
            </button>
          </div>
        </div>
      )}

      {/* Plan cards */}
      <div style={{
        maxWidth: '1050px', margin: '32px auto',
        padding: '0 24px',
        display: 'flex', gap: '20px', justifyContent: 'center',
        flexWrap: 'wrap', alignItems: 'stretch'
      }} className="plan-cards-grid">
        {plans.map(plan => (
          <PlanCard
            key={plan.tier}
            plan={plan}
            isCurrent={plan.tier === currentTier}
            isPopular={plan.tier === popularTier}
            onUpgrade={handleUpgrade}
            upgrading={upgrading === plan.tier}
            canUpgrade={plan.tier !== currentTier}
          />
        ))}
      </div>

      <style>{`
        @media (max-width: 640px) {
          .plan-cards-grid > div {
            flex: 1 1 100% !important;
            max-width: 100% !important;
          }
        }
      `}</style>

      {/* Feature Comparison Table */}
      <div style={{ maxWidth: '1050px', margin: '16px auto', padding: '0 24px' }}>
        <h3 style={{
          textAlign: 'center', color: '#FFFFFF', fontSize: '20px', fontWeight: 700,
          fontFamily: "'Playfair Display', serif", marginBottom: '24px',
        }}>Compare All Features</h3>
        <div style={{
          overflowX: 'auto', borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.06)',
          background: 'rgba(255,255,255,0.02)',
        }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            fontSize: '13px', fontFamily: 'Inter, sans-serif',
          }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <th style={{ textAlign: 'left', padding: '14px 20px', color: '#FFFFFF', fontWeight: 600, fontSize: '13px' }}>Feature</th>
                {plans.map(p => (
                  <th key={p.tier} style={{
                    textAlign: 'center', padding: '14px 16px', color: p.tier === popularTier ? '#D4A853' : '#FFFFFF',
                    fontWeight: 600, fontSize: '13px', textTransform: 'capitalize',
                    background: p.tier === popularTier ? 'rgba(212,168,83,0.06)' : 'transparent',
                  }}>{p.tier}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {plans.length > 0 && (() => {
                // Generate comparison rows from the first plan's structure
                const vendorFeatures = [
                  { key: 'commission', label: 'Commission Rate' },
                  { key: 'portfolio', label: 'Portfolio Images' },
                  { key: 'search', label: 'Search Ranking' },
                  { key: 'verified', label: 'Verified Badge' },
                  { key: 'top_vendor', label: 'Top Vendor Badge' },
                  { key: 'community', label: 'Community Access' },
                  { key: 'event_search', label: 'Event Search' },
                  { key: 'request_board', label: 'Request Board' },
                  { key: 'outreach', label: 'Direct Outreach' },
                  { key: 'loyalty', label: 'Loyalty Points' },
                  { key: 'analytics', label: 'Analytics' },
                ];
                const hostFeatures = [
                  { key: 'rsvp', label: 'RSVPs' },
                  { key: 'invitations', label: 'AI Invitations' },
                  { key: 'registry', label: 'Gift Registry' },
                  { key: 'marketplace', label: 'Vendor Marketplace' },
                  { key: 'tickets', label: 'Ticket Sales' },
                  { key: 'whatsapp', label: 'WhatsApp Messages' },
                  { key: 'seating', label: 'Seating Planner' },
                  { key: 'budget', label: 'Budget Tracker' },
                ];
                const features = planType === 'vendor' ? vendorFeatures : hostFeatures;

                const getFeatureValue = (plan, key) => {
                  if (key === 'commission') return `${plan.commission_rate}%`;
                  if (key === 'portfolio') return plan.max_portfolio_images ? `${plan.max_portfolio_images}` : 'Unlimited';
                  if (key === 'search') return plan.search_ranking === 2 ? 'Top' : plan.search_ranking === 1 ? 'Priority' : 'Standard';
                  if (key === 'verified') return plan.verified_badge ? 'Yes' : '—';
                  if (key === 'top_vendor') return plan.top_vendor_badge ? 'Yes' : '—';
                  if (key === 'community') return plan.community_access ? 'Yes' : '—';
                  if (key === 'event_search') return plan.event_search ? 'Yes' : '—';
                  if (key === 'request_board') return plan.claim_lead_price ? `${formatZAR(plan.claim_lead_price)}/lead` : 'Browse only';
                  if (key === 'outreach') return plan.direct_outreach ? 'Yes' : '—';
                  if (key === 'loyalty') return `${plan.loyalty_multiplier}x`;
                  if (key === 'analytics') return plan.analytics_access ? 'Advanced' : 'Basic';
                  // Host features
                  if (key === 'rsvp') return plan.rsvp_limit ? `${plan.rsvp_limit}` : 'Unlimited';
                  if (key === 'invitations') return plan.invitation_designs ? `${plan.invitation_designs}` : 'Unlimited + AI';
                  if (key === 'registry') return 'Yes';
                  if (key === 'marketplace') return plan.vendor_matching ? 'Full + Priority' : 'Full';
                  if (key === 'tickets') return plan.ticket_sales_limit ? `Up to ${formatZAR(plan.ticket_sales_limit)}` : 'Unlimited';
                  if (key === 'whatsapp') return plan.whatsapp_messages ? `${plan.whatsapp_messages}` : '500';
                  if (key === 'seating') return plan.seating_planner ? 'Yes' : '—';
                  if (key === 'budget') return plan.budget_tracker ? 'Yes' : '—';
                  return '—';
                };

                return features.map((f, idx) => (
                  <tr key={f.key} style={{
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
                  }}>
                    <td style={{ padding: '12px 20px', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>{f.label}</td>
                    {plans.map(p => {
                      const val = getFeatureValue(p, f.key);
                      const isCheck = val === 'Yes' || val === 'Unlimited' || val === 'Unlimited + AI' || val === 'Advanced' || val === 'Full + Priority';
                      const isNeutral = val === '—' || val === 'Standard' || val === 'Basic' || val === 'Browse only';
                      return (
                        <td key={p.tier} style={{
                          textAlign: 'center', padding: '12px 16px', fontSize: '13px',
                          color: isCheck ? '#10B981' : isNeutral ? '#888' : '#D4D4D8',
                          fontWeight: isCheck ? 600 : 400,
                          background: p.tier === popularTier ? 'rgba(212,168,83,0.04)' : 'transparent',
                        }}>
                          {isCheck ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><FiCheck size={13} style={{ color: '#10B981' }} /> {val}</span> : val}
                        </td>
                      );
                    })}
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Social Proof / Testimonials */}
      <div style={{ maxWidth: '1050px', margin: '48px auto', padding: '0 24px' }}>
        <h3 style={{
          textAlign: 'center', color: '#FFFFFF', fontSize: '20px', fontWeight: 700,
          fontFamily: "'Playfair Display', serif", marginBottom: '8px',
        }}>Trusted by Event Hosts & Vendors</h3>
        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '13px', marginBottom: '32px', fontFamily: 'Inter, sans-serif' }}>
          Join thousands across South Africa who plan and grow with Grand Gather Registry
        </p>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            { quote: "Upgrading to Pro was the best decision for my catering business. Lower commissions and priority search ranking brought me 3x more bookings.", name: "Nomvula M.", role: "Catering Vendor, Soweto", rating: 5 },
            { quote: "The Premium plan gave me everything I needed for my wedding — unlimited RSVPs, AI invitations, and the seating planner saved me hours.", name: "Themba K.", role: "Host, Johannesburg", rating: 5 },
            { quote: "Verified badge and direct outreach helped me land corporate clients I wouldn't have reached otherwise. Absolutely worth it.", name: "Priya N.", role: "Decor Vendor, Durban", rating: 5 },
          ].map((t, i) => (
            <div key={i} style={{
              flex: '1 1 280px', maxWidth: '340px', minWidth: '260px',
              borderRadius: '16px', background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)', padding: '24px',
              display: 'flex', flexDirection: 'column', gap: '12px',
            }}>
              <div style={{ display: 'flex', gap: '2px' }}>
                {Array.from({ length: t.rating }).map((_, j) => (
                  <FiStar key={j} size={14} style={{ fill: '#D4A853', color: '#D4A853' }} />
                ))}
              </div>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', lineHeight: 1.6, fontFamily: 'Inter, sans-serif', fontStyle: 'italic', margin: 0, flex: 1 }}>
                "{t.quote}"
              </p>
              <div>
                <p style={{ color: '#FFFFFF', fontSize: '13px', fontWeight: 600, margin: 0, fontFamily: 'Inter, sans-serif' }}>{t.name}</p>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', margin: '2px 0 0', fontFamily: 'Inter, sans-serif' }}>{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Paystack trust footer */}
      <div style={{ textAlign: 'center', padding: '0 24px 48px', opacity: 0.25 }}>
        <p style={{ fontSize: '10px', color: '#FFFFFF', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', margin: '0 0 4px' }}>
          Secured by Paystack
        </p>
        <p style={{ fontSize: '10px', color: '#FFFFFF', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', margin: 0 }}>
          Cards, EFT & more accepted
        </p>
      </div>
    </div>
  );
}
